import { Container } from '@tencent/alloy-renderer';
import {
    ChartTitleComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-title-component';
import {
    ChartBackgroundComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-background-component';
import { RenderPostionType } from '@src/js/constant';
import { SubChartModelObserver } from '@src/slide/application/service/modelObserver/chartObserver';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ENABLE_CHART_RENDER } from '@src/slide/infrastructure/module/dependent/config/renderer/switch';
import { statusReport007 } from '@src/slide/infrastructure/module/dependent/report/real-time-report/real-time';
import {
    Reporter007,
    Reporter007ActionType,
} from '@src/slide/infrastructure/module/dependent/report/real-time-report/constant';
import { CANVAS_CHART_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import type { ChartTitleComponentProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-title-component';
import type { ChartEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/chart-edit-logic';
import type {
    ChartComponentProps,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-component.interface';
import type { ChartBackgroundProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-background-component';
import type { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';

export class RendererChartComponent extends Container {
    // 图形构造入参
    public factoryOptions: InterfaceFactoryOptions<IChartModel>;
    public model: IChartModel;
    public chart: any;
    public chartComponent;
    public chartTitleComponent?: ChartTitleComponent;
    public chartBackgroundComponent: ChartBackgroundComponent;
    public isChartSupported: boolean;
    public isActualRenderChart: boolean;
    public loadChartPromise: Promise<any>;
    // 懒加载的ChartComponent组件
    public ChartComponent;

    // 将observer移动到component的原因是，logic在list和template其他情况下，不会初始化
    private subChartChangeSetObservers: SubChartModelObserver[];
    // 绑定的subCharts关联id，如果id变了表示子表有类型修改
    private subChartRelativeId: string;
    private hasReported: boolean;
    private subChartChangeSetCallBackMap = new Map();

    public constructor(factoryOptions: InterfaceFactoryOptions<IChartModel>) {
        super();
        this.factoryOptions = factoryOptions;
        this.model = factoryOptions.model;
        this.id = `${CANVAS_CHART_ID_PREFIX}-${this.model.key}`;
        this.isChartSupported = this.model.isChartSupported;
        this.isActualRenderChart = this.model.isActualRenderChart;


        this.setCursor();
        this.updateComponent();
        this.reportChartRender();
    }

    // factory层监听model变化，调用此函数刷新元素渲染
    public updateComponent = async (forceUpdate?: boolean) => {
        // 3.0的undoRedo需要绑在chartModel上实现，因为不会有subChartModel不会bindSdK
        this.updateEditorSheetIfUndoRedo();

        await this.loadChartComponent();

        if (this.isActualRenderChart) {
            // 懒加载chartComponent

            if (this.chartComponent) {
                // 先渲染背景
                this.updateBackground();
                // 先更新title
                this.updateTitle();
                // 再渲染图表（g2图表没有标题）
                this.updateChart(forceUpdate);

                // 再渲染title
                this.chartTitleComponent && this.addChild(this.chartTitleComponent);

                return;
            }

            // 创建组件和render分开的原因是，chartComponent需要获取标题的实际高度，来计算实际的g2绘图区域。
            // 但是title的层级在chart之上，所以分开创建和addChild
            this.chartBackgroundComponent = this.createBackgroundComponent();
            this.updateTitle();
            this.chartComponent = this.createChartComponent();
            // 先渲染背景
            this.addChild(this.chartBackgroundComponent);
            // 再渲染图表（g2图表没有标题）
            this.addChild(this.chartComponent);
            // 再渲染标题
            this.chartTitleComponent && this.addChild(this.chartTitleComponent);
        } else {
            // 不支持图表展示的时候，只实例化chartComponent。因为其本身就是个Image类，会在其中去draw一张兜底图
            if (this.chartComponent) {
                this.updateChart(forceUpdate);
                return;
            }

            this.chartComponent = this.createChartComponent();
            this.addChild(this.chartComponent);
        }
    };

    public dispose() {
        super.dispose();

        this.removeChangeSetObserver();
    }

    private updateEditorSheetIfUndoRedo() {
        // 有changeset的变更，且图表编辑面板还存在，需要重新将新的 sdk 值转化并同步给面板数据
        const chartManager = this.logic?.chartManager;
        // TODO 这里有遗留的问题，现在编辑窗口在的时候，还是能修改图表的属性。当属性/数据变更之后，需要更新sheetInstance
        // 但是spreadsheetInstance.loadData 会导致选区重置，编辑体验很糟糕。目前spreed-sheet这个库master上的不是最新包代码（本地有未提交）
        // 所以这里先不调用更新。
        if (this.model.undoRedo && chartManager?.isShowChartEditModal) {
            this.updateSheetInstance();
        }
    }

    private addSubChartChangeSetObserver = () => {
        const { model } = this.factoryOptions;
        const subCharts = model.plotArea?.charts;

        if (subCharts && subCharts.length > 0) {
            this.subChartChangeSetObservers = subCharts.map(subChartModel => new SubChartModelObserver(subChartModel));
            /* eslint-disable */ // eslint 在 fix 时的 bug
            this.subChartChangeSetObservers.map((ob, index) => {
                // 回调里需要获取subChartModel, 所以需要在这里进行传入
                // 通过map把回调存起来，保证addChangesetObserver、removeChangesetObserver接受同一个函数
                const cbFn = () => this.subChartChangeSetCallback(ob.model);
                this.subChartChangeSetCallBackMap.set(index, cbFn);
                return ob.addChangesetObserver(cbFn);
            }
            );
        }
    }

    private subChartChangeSetCallback = (model: Record<string, any>) => {
        this.updateComponent(true);

        // 有changeset的变更，且图表编辑面板还存在，需要重新将新的 sdk 值转化并同步给面板数据
        const chartManager = this.logic?.chartManager;
        // TODO 这里有遗留的问题，现在编辑窗口在的时候，还是能修改图表的属性。当属性/数据变更之后，需要更新sheetInstance
        // 但是spreadsheetInstance.loadData 会导致选区重置，编辑体验很糟糕。目前spreed-sheet这个库master上的不是最新包代码（本地有未提交）
        // 所以这里先不调用更新。
        if (model.undoRedo && chartManager?.isShowChartEditModal) {
            this.updateSheetInstance();
        }
    }

    public updateSubChartChangeSetObserver() {
        this.removeChangeSetObserver();

        this.addSubChartChangeSetObserver();
    }

    private updateSheetInstance() {
        const chartManager = this.logic?.chartManager;
        if (chartManager) {
            const newChartTableData = chartManager.convertChartDataFromSDK();

            chartManager.spreadsheetInstance?.loadData([
                {
                    rows: newChartTableData,
                },
            ]);
        }
    }

    private removeChangeSetObserver() {
        this.subChartChangeSetObservers?.map((ob, index) => {
            const cbFn = this.subChartChangeSetCallBackMap.get(index);
            ob.removeChangesetObserver(cbFn);
        });
        this.subChartChangeSetCallBackMap.clear();
    }

    private getBackgroundComponentProps = (): ChartBackgroundProps => ({
        frame: {
            height: this.factoryOptions.frame?.height as number,
            width: this.factoryOptions.frame?.width as number,
            top: 0,
            left: 0,
        },
        style: this.model.chartSpace?.props?.style,
        factoryOptions: this.factoryOptions,
    });

    private getChartComponentProps = (): ChartComponentProps => ({
        frame: this.factoryOptions.frame!,
        model: this.model,
        factoryOptions: this.factoryOptions,
        titleRect: this.chartTitleComponent?.getRealTitleRect() || { left: 0, top: 0, height: 0, width: 0 },
        wholeChart: this,
    });

    private getChartTitleComponentProps = (): ChartTitleComponentProps => ({
        frame: this.factoryOptions.frame!,
        model: this.model,
        factoryOptions: this.factoryOptions,
    });

    private createBackgroundComponent = () => new ChartBackgroundComponent(this.getBackgroundComponentProps());
    private createChartComponent = () => new this.ChartComponent(this.getChartComponentProps());
    private createTitleComponent = (): ChartTitleComponent => {
        return new ChartTitleComponent(this.getChartTitleComponentProps());
    }

    private updateChart = (forceUpdate?: boolean) => {
        this.chartComponent.updateProps(this.getChartComponentProps(), forceUpdate);
    };

    private get logic(): ChartEditLogic {
        return this.factoryOptions.logicService.getLogic(this.factoryOptions) as ChartEditLogic;
    }

    private updateTitle = () => {
        if (this.model.title !== undefined) {
            if (this.chartTitleComponent) {
                this.chartTitleComponent.updateProps(this.getChartTitleComponentProps());
            } else {
                this.chartTitleComponent = this.createTitleComponent();
            }
        } else if (this.chartTitleComponent) {
            this.removeChild(this.chartTitleComponent);
            this.chartTitleComponent.dispose();
            this.chartTitleComponent = undefined;
        }
    };

    private updateBackground = () => {
        this.chartBackgroundComponent.updateProps(this.getBackgroundComponentProps());
    };

    private loadChartComponent = (): Promise<any> => {
        if (!this.loadChartPromise) {
            this.loadChartPromise = import(/* webpackChunkName: "chart-component" */ '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component');

            this.loadChartPromise.then((module) => {
                if (!this.ChartComponent) {
                    this.ChartComponent = module.ChartContainer;
                }
            });
        }

        return this.loadChartPromise;
    };

    private setCursor() {
        if(this.factoryOptions.region === RenderPostionType.PLAYER) {
            this.cursor = 'auto'
        }
    }

    private reportChartRender() {
        const isMainScene =  this.factoryOptions.region === RenderPostionType.CANVAS
            || this.factoryOptions.region === RenderPostionType.PLAYER
            || this.factoryOptions.region === RenderPostionType.MOBILEBROWSE;

        if (!isMainScene || this.hasReported) {
            return;
        }

        const subChartType = this.model.plotArea?.charts.map(chart => chart.type).join('-');
        const ver6Param = JSON.stringify({
            type: subChartType,
            enableRender: ENABLE_CHART_RENDER,
            actualRenderChart: this.isActualRenderChart,
            fallbackImageSrc: this.model.fallbackImageSrc,
            region: this.factoryOptions.region,
        });

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.CHART,
            action: this.isActualRenderChart ? TDWActionType.RENDER_CHART : TDWActionType.RENDER_FALLBACK,
            ver6: ver6Param,
        });

        statusReport007(
            Reporter007ActionType.CHART_RENDER_STATUS,
            this.isActualRenderChart ? Reporter007.SUCCESS : Reporter007.ERROR,
            subChartType,
        );

        this.hasReported = true;
    }
}
